import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, CheckCircle2 } from "lucide-react";
import { WHY_CHOOSE_FAQ } from "../lib/constants";

const HIGHLIGHTS = [
  "24/7 platform monitoring",
  "FX + engineering in one team",
  "Scope-aligned delivery",
];

export default function WhyChooseUs() {
  const [open, setOpen] = useState(0);

  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative theme-section py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 dot-grid opacity-30 pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00C2FF]/50 to-transparent" />
      <div className="absolute -top-24 right-0 w-[420px] h-[420px] bg-[#003E8C] rounded-full blur-[180px] opacity-35 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left — header + highlights */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px bg-[#00C2FF]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#00C2FF] font-bold">
                / Why us
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[1.08] tracking-tighter">
              What trading desks
              <br />
              <span className="text-gradient-cyan italic">value in us</span>
            </h2>
            <p className="text-gray-400 text-sm md:text-base leading-relaxed mt-5 max-w-md">
              From launch to scale — transparent delivery, senior talent, and infrastructure
              built for regulated FX desks.
            </p>

            <ul className="mt-8 space-y-3">
              {HIGHLIGHTS.map((text) => (
                <li key={text} className="flex items-center gap-3 text-sm text-gray-300">
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-[#00C2FF]" strokeWidth={2.5} />
                  {text}
                </li>
              ))}
            </ul>

            <div className="hidden sm:flex mt-10 glass-panel rounded-2xl p-5 border-[#00C2FF]/20 max-w-xs">
              <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#9FE3FF] mb-2">
                {String(WHY_CHOOSE_FAQ.length).padStart(2, "0")} reasons
              </p>
              <p className="text-gray-400 text-xs leading-relaxed">
                Expand each point for how we support your desk day to day.
              </p>
            </div>
          </motion.div>

          {/* Right — accordion list */}
          <div className="lg:col-span-7 space-y-2">
            {WHY_CHOOSE_FAQ.map((item, i) => {
              const isOpen = open === i;
              const num = String(i + 1).padStart(2, "0");

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.45 }}
                  className={`group relative rounded-2xl border transition-all duration-300 ${
                    isOpen
                      ? "theme-accordion-open shadow-[0_8px_40px_rgba(0,194,255,0.12)]"
                      : "theme-accordion-idle"
                  }`}
                >
                  <span
                    className={`absolute left-0 top-4 bottom-4 w-1 rounded-full transition-all duration-300 ${
                      isOpen ? "bg-gradient-to-b from-[#00C2FF] to-[#9FE3FF]" : "bg-white/10"
                    }`}
                    aria-hidden
                  />

                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    data-testid={`faq-toggle-${i}`}
                    aria-expanded={isOpen}
                    className="w-full flex items-start gap-4 sm:gap-5 pl-5 pr-5 sm:pr-6 py-5 sm:py-6 text-left"
                  >
                    <span
                      className={`font-mono text-xs font-bold tabular-nums pt-0.5 transition-colors ${
                        isOpen ? "text-[#00C2FF]" : "text-slate-400 dark:text-white/25 group-hover:text-slate-500 dark:group-hover:text-white/40"
                      }`}
                    >
                      {num}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span
                        className={`font-display block text-base md:text-lg font-semibold leading-snug transition-colors ${
                          isOpen ? "theme-heading" : "theme-subtle"
                        }`}
                      >
                        {item.q}
                      </span>
                    </span>
                    <span
                      className={`mt-0.5 shrink-0 w-8 h-8 rounded-lg border flex items-center justify-center transition-all duration-300 ${
                        isOpen
                          ? "border-[#00C2FF]/50 bg-[#00C2FF]/10 rotate-180"
                          : "border-slate-200 dark:border-white/15 bg-slate-50 dark:bg-white/[0.03] group-hover:border-[#9FE3FF]/30"
                      }`}
                    >
                      <ChevronDown
                        className={`w-4 h-4 transition-colors ${
                          isOpen ? "text-[#00C2FF]" : "text-slate-500 dark:text-white/50"
                        }`}
                      />
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pl-14 sm:pl-[4.25rem] pr-5 sm:pr-6 pb-5 sm:pb-6 -mt-1">
                          <p className="theme-muted text-sm leading-relaxed border-t theme-divider pt-4">
                            {item.a}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
